import React from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Divider, Grid } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { BuildingCode, DesignStandard, getTpi1Year, isTpi1VersionOrLater, tpiSection } from '@paragon/contract-library';

import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { ValidDesignCodes } from '../validDesignCodes';
import { DurationFactorSetting } from './DurationFactorSettings';

function getCurrentDesignStandardByBuildingCode() {
  const result = new Map<BuildingCode, DesignStandard>();
  for (const code of ValidDesignCodes) {
    const [buildingCode, designStandard] = code;
    const oldMax = result.get(buildingCode);
    if (oldMax == null || (getTpi1Year(designStandard) ?? 0) > (getTpi1Year(oldMax) ?? 0))
      result.set(buildingCode, designStandard);
  }
  return result;
}

export const currentDesignStandardByBuildingCode = getCurrentDesignStandardByBuildingCode();

export function PersonLoadSettings({ disabled, loadSettingsArray, setLoadSettingsValues }: LoadSettingsControlProps) {
  const loadSettingsSinceTpi1_2022 = loadSettingsArray.filter((loadSettings) => {
    const designStandard = currentDesignStandardByBuildingCode.get(loadSettings.buildingCode);
    return designStandard != null && isTpi1VersionOrLater(designStandard, 2022);
  });
  const referenceStandard =
    (loadSettingsSinceTpi1_2022.length &&
      currentDesignStandardByBuildingCode.get(loadSettingsSinceTpi1_2022[0].buildingCode)) ||
    DesignStandard.Tpi2022;

  return (
    <Grid size={12}>
      <Box>
        <Divider />
        <MultiValueBooleanInput
          sx={{ m: 0 }}
          values={loadSettingsSinceTpi1_2022.map((loadSettings) => loadSettings.considerPersonLoads)}
          onChange={(considerPersonLoads) => setLoadSettingsValues({ considerPersonLoads })}
          label="Person loading (if required)"
          disabled={disabled}
        />
        <TooltipIconButton
          title={
            <>
              Applies a moving person load if required by the applicable design standard. See{' '}
              {tpiSection('6.2.2.5', referenceStandard)}.
            </>
          }
        >
          <HelpIcon />
        </TooltipIconButton>
      </Box>
      <Box>
        <MultiValueBooleanInput
          sx={{ m: 0 }}
          values={loadSettingsSinceTpi1_2022.map(
            (loadSettings) => loadSettings.useCommitteeInterpretationForPersonLoads,
          )}
          onChange={(useCommitteeInterpretationForPersonLoads) =>
            setLoadSettingsValues({ useCommitteeInterpretationForPersonLoads })
          }
          label="Use TPI Technical Advisory Committee Interpretation"
          disabled={disabled || loadSettingsSinceTpi1_2022.every((loadSettings) => !loadSettings.considerPersonLoads)}
        />
        <TooltipIconButton
          title={
            <>
              Applies the technical interpretation issued by TPI&rsquo;s Technical Advisory Committee to reduce or
              eliminate person loads in some cases. For details, see the interpretation document available from{' '}
              <a href="https://www.tpinst.org/freedownloads" target="_blank" rel="noreferrer">
                https://www.tpinst.org/freedownloads
              </a>
              .
            </>
          }
          enableInteractive
        >
          <HelpIcon />
        </TooltipIconButton>
      </Box>
      <DurationFactorSetting
        sx={{ width: '100%' }}
        loadType="person"
        loadSettingsArray={loadSettingsArray}
        setLoadSettingsValues={setLoadSettingsValues}
        disabled={disabled}
      />
    </Grid>
  );
}
